import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Stack,
  Breadcrumbs,
  Link,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  createProduct,
  updateProduct,
  deleteProduct,
  getProductCatalog,
  syncProductOptions,
  purgeLegacySKUs,
  setProductCategories,
  setProductAttributeValues,
  getInheritedAttributes,
  getInheritedVariableCosts,
  getProductEditHistory,
  updateSKU,
  deleteSKU,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { useExtensions } from '../../../app/ExtensionsContext';
import MediaGallery from '../../medias/public/MediaGallery';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import { OptionDefaultsForExistingDialog, detectNewOptions, optionsNeedExistingSkuDefaults } from '../../core/public';
import ProductEditHistoryTimeline from '../public/ProductEditHistoryTimeline';
import ProductSpecificationsSection from '../private/ProductSpecificationsSection';
import { CategoryMultiSelect } from '../../categories/public';
import VariantOptionsDialog from '../private/VariantOptionsDialog';
import ProductVariantsTable from '../private/ProductVariantsTable';
import VariantDetailDialog from '../private/VariantDetailDialog';
import VariantBulkEditDialog from '../private/VariantBulkEditDialog';
import CustomizationFieldsSection from '../private/CustomizationFieldsSection';
import ProductVariableCostsSection from '../private/ProductVariableCostsSection';
import {
  normalizeCatalog,
  optionsToSyncPayload,
  buildSkuUpdatePayload,
  partitionVariants,
  isVariantReadOnly,
} from '../private/productVariantUtils';
import {
  applyProductVariableCostsToVariant,
  buildMetadataPatch,
  buildProductDefaultsMetadataPatch,
  getProductVariableCostDefaults,
  getSavedSimulation,
  PRICE_SIMULATOR_EXTENSION_KEY,
  resolveCostForSimulationApply,
  resolveEffectiveVariableCostDefaults,
} from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';
import { invalidateProductSkuPickerCache } from '../private/productSkuPickerCache';

const VARIANT_PATCH_DEBOUNCE_MS = 600;
const FORM_AUTOSAVE_DEBOUNCE_MS = 700;

const ProductFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { translate, language } = useTranslation();

  const [loading, setLoading] = useState(isEdit);
  const [tab, setTab] = useState('cadastro');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState({ name: '', description: '', status: 'ACTIVE' });
  const [productId, setProductId] = useState(null);
  const [productCode, setProductCode] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [inheritedAttributes, setInheritedAttributes] = useState([]);
  const [inheritedAttributesLoading, setInheritedAttributesLoading] = useState(false);
  const [attributeValues, setAttributeValues] = useState({});
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [legacyCount, setLegacyCount] = useState(0);
  const [legacyPreview, setLegacyPreview] = useState([]);
  const [customizationFields, setCustomizationFields] = useState([]);
  const [groupByIndex, setGroupByIndex] = useState(0);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [optionsSaving, setOptionsSaving] = useState(false);
  const [detailVariant, setDetailVariant] = useState(null);
  const [detailSaving, setDetailSaving] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [errors] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [optionDefaultsOpen, setOptionDefaultsOpen] = useState(false);
  const [pendingOptionsDraft, setPendingOptionsDraft] = useState(null);
  const [pendingValueRenames, setPendingValueRenames] = useState([]);
  const [confirmPurgeLegacy, setConfirmPurgeLegacy] = useState(false);
  const [purgingLegacy, setPurgingLegacy] = useState(false);
  const [variableCostDefaults, setVariableCostDefaults] = useState([]);
  const [inheritedVariableCosts, setInheritedVariableCosts] = useState([]);

  const { isEnabled } = useExtensions();
  const simulatorEnabled = isEnabled(PRICE_SIMULATOR_EXTENSION_KEY);

  const variantPatchTimers = useRef(new Map());
  const variableCostsSaveTimer = useRef(null);
  const variableCostDefaultsRef = useRef(variableCostDefaults);
  const variantsRef = useRef(variants);
  const pendingVariantPatches = useRef(new Map());
  const savingVariantIdsRef = useRef(new Set());
  const formSaveTimer = useRef(null);
  const categoriesSaveTimer = useRef(null);
  const attributeValuesSaveTimer = useRef(null);
  const savedToastDebounceRef = useRef(null);
  const creatingRef = useRef(false);
  const newProductAutoKickoffRef = useRef(false);
  const [newProductCreateError, setNewProductCreateError] = useState(null);

  const showError = useCallback((message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const queueSavedToast = useCallback(() => {
    if (savedToastDebounceRef.current) clearTimeout(savedToastDebounceRef.current);
    savedToastDebounceRef.current = setTimeout(() => {
      setSnackbar({
        open: true,
        message: translate('common.saved'),
        severity: 'success',
        autoHideDuration: 2200,
      });
      savedToastDebounceRef.current = null;
    }, 450);
  }, [translate]);

  useEffect(() => {
    return () => {
      if (savedToastDebounceRef.current) {
        clearTimeout(savedToastDebounceRef.current);
        savedToastDebounceRef.current = null;
      }
    };
  }, []);

  const inheritedContextKeyRef = useRef('');

  const effectiveVariableCostDefaults = useMemo(
    () => resolveEffectiveVariableCostDefaults({
      inheritedCategoryRows: inheritedVariableCosts,
      productRows: variableCostDefaults,
    }),
    [inheritedVariableCosts, variableCostDefaults],
  );

  const applyCatalogCategories = useCallback(async (categories, attributeValueRows) => {
    const sorted = [...(categories || [])].sort((a, b) => a.display_order - b.display_order);
    setSelectedCategories(
      sorted.map((c) => ({
        id: c.category_id,
        name: c.name,
        slug: c.slug,
      })),
    );
    const valuesMap = {};
    (attributeValueRows || []).forEach((row) => {
      if (row.attribute_specification_id) {
        valuesMap[row.attribute_specification_id] = row.value ?? '';
      }
    });
    setAttributeValues(valuesMap);
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!isEdit || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [catalogRes, historyRes] = await Promise.all([
        getProductCatalog(id),
        getProductEditHistory(id).catch(() => ({ data: [] })),
      ]);
      const catalog = normalizeCatalog(catalogRes.data);
      if (!catalog) throw new Error(translate('products.not_found'));
      setProductId(catalog.id);
      setProductCode(catalog.code);
      setForm({
        name: catalog.name,
        description: catalog.description,
        status: catalog.status,
      });
      setOptions(catalog.options);
      setVariants(catalog.variants);
      setLegacyCount(catalog.legacy_count ?? 0);
      setLegacyPreview(catalog.legacy_preview ?? []);
      setCustomizationFields(catalog.customization_fields);
      setVariableCostDefaults(getProductVariableCostDefaults(catalog.metadata || {}));
      inheritedContextKeyRef.current = '';
      await applyCatalogCategories(catalog.categories, catalog.attribute_values);
      setEditHistory(historyRes.data || []);
    } catch (err) {
      setLoadError(err.message || translate('products.catalog_load_error'));
      showError(err.message || translate('products.catalog_load_error'));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, applyCatalogCategories, translate, showError]);

  useEffect(() => {
    if (isEdit) loadCatalog();
  }, [isEdit, loadCatalog]);

  const mainCategoryId = selectedCategories[0]?.id || '';

  useEffect(() => {
    if (!isEdit) return;

    const contextKey = `${productId || ''}:${mainCategoryId}:${simulatorEnabled ? '1' : '0'}`;
    if (inheritedContextKeyRef.current === contextKey) return;
    inheritedContextKeyRef.current = contextKey;

    let cancelled = false;

    const loadInherited = async () => {
      if (!mainCategoryId) {
        setInheritedAttributes([]);
        setInheritedVariableCosts([]);
        setInheritedAttributesLoading(false);
        return;
      }

      setInheritedAttributesLoading(true);
      try {
        const [attrRes, costRes] = await Promise.all([
          getInheritedAttributes(mainCategoryId),
          simulatorEnabled
            ? getInheritedVariableCosts(mainCategoryId)
            : Promise.resolve({ data: { defaults: [] } }),
        ]);
        if (cancelled) return;

        const inherited = costRes.data?.defaults ?? [];
        setInheritedAttributes(attrRes.data?.attributes ?? []);
        setInheritedVariableCosts(inherited);

        if (simulatorEnabled && productId) {
          const effective = resolveEffectiveVariableCostDefaults({
            inheritedCategoryRows: inherited,
            productRows: variableCostDefaultsRef.current,
          });
          setVariants((prev) => prev.map((variant) => applyProductVariableCostsToVariant(variant, effective)));
          setDetailVariant((prev) => (prev ? applyProductVariableCostsToVariant(prev, effective) : prev));
        }
      } catch (err) {
        if (cancelled) return;
        inheritedContextKeyRef.current = '';
        setInheritedAttributes([]);
        setInheritedVariableCosts([]);
        showError(err.message);
      } finally {
        if (!cancelled) setInheritedAttributesLoading(false);
      }
    };

    void loadInherited();
    return () => {
      cancelled = true;
    };
  }, [isEdit, productId, mainCategoryId, simulatorEnabled, showError]);

  const createProductAutomatically = useCallback(async () => {
    if (creatingRef.current || isEdit) return false;
    creatingRef.current = true;
    setNewProductCreateError(null);
    try {
      const res = await createProduct({
        name: '',
        description: '',
        status: 'ACTIVE',
        type: 'PRODUCT',
        locale: language,
      });
      const created = res.data;
      invalidateProductSkuPickerCache();
      navigate(`/products/${encodeURIComponent(created.code || created.id)}/edit`, { replace: true });
      return true;
    } catch (err) {
      setNewProductCreateError(err.message || translate('products.new_product_create_failed'));
      return false;
    } finally {
      creatingRef.current = false;
    }
  }, [isEdit, language, navigate, translate]);

  useEffect(() => {
    if (isEdit) return;
    if (newProductAutoKickoffRef.current) return;
    newProductAutoKickoffRef.current = true;
    void createProductAutomatically();
  }, [isEdit, createProductAutomatically]);

  useEffect(() => {
    setFilteredVariants(partitionVariants(variants, options).editable);
  }, [variants, options]);

  useEffect(() => {
    variantsRef.current = variants;
  }, [variants]);

  useEffect(() => {
    variableCostDefaultsRef.current = variableCostDefaults;
  }, [variableCostDefaults]);

  const persistProduct = useCallback(
    async (nextForm) => {
      if (!isEdit || !id) return;
      if (!nextForm.name.trim()) return;
      try {
        await updateProduct(id, {
          name: nextForm.name.trim(),
          description: nextForm.description.trim(),
          status: nextForm.status,
        });
        invalidateProductSkuPickerCache();
        queueSavedToast();
      } catch (err) {
        showError(err.message);
      }
    },
    [id, isEdit, queueSavedToast, showError],
  );

  const scheduleProductSave = useCallback(
    (nextForm) => {
      if (formSaveTimer.current) clearTimeout(formSaveTimer.current);
      formSaveTimer.current = setTimeout(() => {
        void persistProduct(nextForm);
      }, FORM_AUTOSAVE_DEBOUNCE_MS);
    },
    [persistProduct],
  );

  const persistAttributeValues = useCallback(
    async (valuesMap, attrs) => {
      if (!productId) return;
      try {
        const values = (attrs || inheritedAttributes).map((attr) => ({
          attribute_specification_id: attr.id ?? attr.ID ?? '',
          value: valuesMap[attr.id ?? attr.ID ?? ''] ?? '',
        }));
        await setProductAttributeValues(productId, { values });
        invalidateProductSkuPickerCache();
        queueSavedToast();
      } catch (err) {
        showError(err.message);
      }
    },
    [productId, inheritedAttributes, queueSavedToast, showError],
  );

  const scheduleAttributeValuesSave = useCallback(
    (valuesMap) => {
      if (attributeValuesSaveTimer.current) clearTimeout(attributeValuesSaveTimer.current);
      attributeValuesSaveTimer.current = setTimeout(() => {
        void persistAttributeValues(valuesMap);
      }, FORM_AUTOSAVE_DEBOUNCE_MS);
    },
    [persistAttributeValues],
  );

  const handleFormChange = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (isEdit) scheduleProductSave(next);
      return next;
    });
  };

  const handleCategoriesChange = (categories) => {
    setSelectedCategories(categories);
    inheritedContextKeyRef.current = '';
    const nextMainId = categories[0]?.id || '';
    if (!nextMainId) setAttributeValues({});
    if (isEdit && productId) scheduleCategoriesSave(categories);
  };

  const handleAttributeValueChange = (attrId, value) => {
    setAttributeValues((prev) => {
      const next = { ...prev, [attrId]: value };
      if (isEdit && productId) scheduleAttributeValuesSave(next);
      return next;
    });
  };

  const syncVariantSimulationsWithVariableCosts = useCallback(
    async (rows, variantList) => {
      const toPersist = variantList
        .map((variant) => {
          if (!getSavedSimulation(variant.metadata)) return null;
          if (isVariantReadOnly(variant, options)) return null;
          const patched = applyProductVariableCostsToVariant(variant, rows);
          return {
            variant,
            patched,
            payload: buildSkuUpdatePayload(
              variant,
              {
                price: patched.price,
                cost: patched.cost,
                metadata: patched.metadata,
              },
              options,
            ),
          };
        })
        .filter(Boolean);

      if (!toPersist.length) return;

      try {
        await Promise.all(
          toPersist.map(({ variant, payload }) => updateSKU(variant.id, payload)),
        );
      } catch (err) {
        showError(err.message);
      }
    },
    [options, showError],
  );

  const persistCategories = useCallback(
    async (categories) => {
      if (!productId) return;
      try {
        await setProductCategories(productId, {
          categories: categories.map((c, index) => ({
            category_id: c.id,
            order: index,
            is_main: index === 0,
          })),
        });
        const mainId = categories[0]?.id || '';
        setInheritedAttributesLoading(true);
        try {
          const [attrRes, costRes] = await Promise.all([
            mainId
              ? getInheritedAttributes(mainId)
              : Promise.resolve({ data: { attributes: [] } }),
            mainId && simulatorEnabled
              ? getInheritedVariableCosts(mainId)
              : Promise.resolve({ data: { defaults: [] } }),
          ]);
          const inherited = costRes.data?.defaults ?? [];
          setInheritedAttributes(attrRes.data?.attributes ?? []);
          setInheritedVariableCosts(inherited);
          inheritedContextKeyRef.current = `${productId || ''}:${mainId}:${simulatorEnabled ? '1' : '0'}`;
          if (simulatorEnabled) {
            const effective = resolveEffectiveVariableCostDefaults({
              inheritedCategoryRows: inherited,
              productRows: variableCostDefaults,
            });
            const patchedVariants = variantsRef.current.map((variant) =>
              applyProductVariableCostsToVariant(variant, effective),
            );
            setVariants(patchedVariants);
            setDetailVariant((prev) => {
              if (!prev) return prev;
              return patchedVariants.find((v) => v.id === prev.id) ?? prev;
            });
            await syncVariantSimulationsWithVariableCosts(effective, patchedVariants);
          }
        } finally {
          setInheritedAttributesLoading(false);
        }
        invalidateProductSkuPickerCache();
        queueSavedToast();
      } catch (err) {
        showError(err.message);
      }
    },
    [
      productId,
      simulatorEnabled,
      variableCostDefaults,
      syncVariantSimulationsWithVariableCosts,
      queueSavedToast,
      showError,
    ],
  );

  const scheduleCategoriesSave = useCallback(
    (categories) => {
      if (categoriesSaveTimer.current) clearTimeout(categoriesSaveTimer.current);
      categoriesSaveTimer.current = setTimeout(() => {
        void persistCategories(categories);
      }, FORM_AUTOSAVE_DEBOUNCE_MS);
    },
    [persistCategories],
  );

  const applyVariableCostsToLocalVariants = useCallback((rows) => {
    setVariants((prev) => prev.map((variant) => applyProductVariableCostsToVariant(variant, rows)));
    setDetailVariant((prev) => (prev ? applyProductVariableCostsToVariant(prev, rows) : prev));
  }, []);

  const persistVariableCostDefaults = useCallback(
    async (rows) => {
      if (!isEdit || !id) return;
      try {
        const effective = resolveEffectiveVariableCostDefaults({
          inheritedCategoryRows: inheritedVariableCosts,
          productRows: rows,
        });
        await updateProduct(id, {
          name: form.name.trim(),
          description: form.description.trim(),
          status: form.status,
          metadata: buildProductDefaultsMetadataPatch(rows),
        });
        const patchedVariants = variantsRef.current.map((variant) =>
          applyProductVariableCostsToVariant(variant, effective),
        );
        setVariants(patchedVariants);
        setDetailVariant((prev) => {
          if (!prev) return prev;
          return patchedVariants.find((v) => v.id === prev.id) ?? prev;
        });
        await syncVariantSimulationsWithVariableCosts(effective, patchedVariants);
        invalidateProductSkuPickerCache();
        queueSavedToast();
      } catch (err) {
        showError(err.message);
      }
    },
    [
      id,
      isEdit,
      form.name,
      form.description,
      form.status,
      inheritedVariableCosts,
      queueSavedToast,
      syncVariantSimulationsWithVariableCosts,
      showError,
    ],
  );

  const scheduleVariableCostDefaultsSave = useCallback(
    (rows) => {
      if (variableCostsSaveTimer.current) clearTimeout(variableCostsSaveTimer.current);
      variableCostsSaveTimer.current = setTimeout(() => {
        void persistVariableCostDefaults(rows);
      }, FORM_AUTOSAVE_DEBOUNCE_MS);
    },
    [persistVariableCostDefaults],
  );

  const handleVariableCostDefaultsChange = (rows) => {
    setVariableCostDefaults(rows);
    const effective = resolveEffectiveVariableCostDefaults({
      inheritedCategoryRows: inheritedVariableCosts,
      productRows: rows,
    });
    applyVariableCostsToLocalVariants(effective);
    if (isEdit && productId) scheduleVariableCostDefaultsSave(rows);
  };

  useEffect(() => {
    return () => {
      if (variableCostsSaveTimer.current) clearTimeout(variableCostsSaveTimer.current);
    };
  }, []);

  const handleOptionsSave = async (draftOptions, defaultValuesForExisting = {}, valueRenames = []) => {
    if (!productId) return;
    setOptionsSaving(true);
    try {
      await syncProductOptions(
        productId,
        optionsToSyncPayload(draftOptions, defaultValuesForExisting, valueRenames),
      );
      setOptionsDialogOpen(false);
      setOptionDefaultsOpen(false);
      setPendingOptionsDraft(null);
      setPendingValueRenames([]);
      invalidateProductSkuPickerCache();
      await loadCatalog();
      setSnackbar({ open: true, message: translate('products.options_saved'), severity: 'success' });
    } catch (err) {
      showError(err.message);
    } finally {
      setOptionsSaving(false);
    }
  };

  const handleOptionsSaveRequest = (draftOptions, valueRenames = []) => {
    if (optionsNeedExistingSkuDefaults(options, draftOptions, variants)) {
      setPendingOptionsDraft(draftOptions);
      setPendingValueRenames(valueRenames);
      setOptionDefaultsOpen(true);
      return;
    }
    void handleOptionsSave(draftOptions, {}, valueRenames);
  };

  const handleOptionDefaultsConfirm = (defaults) => {
    if (!pendingOptionsDraft) return;
    void handleOptionsSave(pendingOptionsDraft, defaults, pendingValueRenames);
  };

  const handlePurgeLegacy = async () => {
    if (!productId) return;
    setPurgingLegacy(true);
    try {
      const res = await purgeLegacySKUs(productId);
      const deleted = res.data?.deleted ?? 0;
      const retained = res.data?.retained ?? 0;
      invalidateProductSkuPickerCache();
      await loadCatalog();
      setConfirmPurgeLegacy(false);
      const msg = translate('products.purge_legacy_success')
        .replace('{deleted}', String(deleted))
        .replace('{retained}', String(retained));
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (err) {
      showError(err.message);
    } finally {
      setPurgingLegacy(false);
    }
  };

  const flushVariantPatch = useCallback(
    async (variantId, patch) => {
      const current = variants.find((v) => v.id === variantId);
      if (!current) return;
      if (savingVariantIdsRef.current.has(variantId)) return;
      savingVariantIdsRef.current.add(variantId);
      try {
        await updateSKU(variantId, buildSkuUpdatePayload(current, patch, options));
        invalidateProductSkuPickerCache();
        queueSavedToast();
      } catch (err) {
        showError(err.message);
      } finally {
        savingVariantIdsRef.current.delete(variantId);
      }
    },
    [variants, options, queueSavedToast, showError],
  );

  const handleVariantPatch = (variantId, patch) => {
    const current = variants.find((v) => v.id === variantId);
    if (isVariantReadOnly(current, options)) return;
    setVariants((prev) =>
      prev.map((v) => (v.id === variantId ? { ...v, ...patch } : v)),
    );
    pendingVariantPatches.current.set(variantId, {
      ...(pendingVariantPatches.current.get(variantId) || {}),
      ...patch,
    });
    const existing = variantPatchTimers.current.get(variantId);
    if (existing) clearTimeout(existing);
    variantPatchTimers.current.set(
      variantId,
      setTimeout(() => {
        variantPatchTimers.current.delete(variantId);
        const merged = pendingVariantPatches.current.get(variantId) || {};
        pendingVariantPatches.current.delete(variantId);
        void flushVariantPatch(variantId, merged);
      }, VARIANT_PATCH_DEBOUNCE_MS),
    );
  };

  const handleApplySimulatorPrice = useCallback(
    async (variantId, { price, simulation }) => {
      const current = variants.find((v) => v.id === variantId);
      if (!current || isVariantReadOnly(current, options)) return;

      const existingTimer = variantPatchTimers.current.get(variantId);
      if (existingTimer) clearTimeout(existingTimer);
      variantPatchTimers.current.delete(variantId);
      pendingVariantPatches.current.delete(variantId);

      const metadata = {
        ...(current.metadata || {}),
        ...buildMetadataPatch(simulation),
      };
      const cost = resolveCostForSimulationApply(simulation, current.cost ?? 0);

      setVariants((prev) =>
        prev.map((v) => (v.id === variantId ? { ...v, price, cost, metadata } : v)),
      );
      setDetailVariant((prev) =>
        prev?.id === variantId ? { ...prev, price, cost, metadata } : prev,
      );

      if (savingVariantIdsRef.current.has(variantId)) return;
      savingVariantIdsRef.current.add(variantId);
      try {
        await updateSKU(variantId, buildSkuUpdatePayload(current, { price, cost, metadata }, options));
        invalidateProductSkuPickerCache();
        setSnackbar({
          open: true,
          message: translate('extensions.price_simulator.apply_price_sku_success'),
          severity: 'success',
          autoHideDuration: 2200,
        });
      } catch (err) {
        showError(err.message);
      } finally {
        savingVariantIdsRef.current.delete(variantId);
      }
    },
    [variants, options, translate, showError],
  );

  const handleDetailSave = async (variantId, draft) => {
    const current = variants.find((v) => v.id === variantId);
    if (isVariantReadOnly(current, options)) return;
    setDetailSaving(true);
    try {
      const current = variants.find((v) => v.id === variantId);
      await updateSKU(variantId, buildSkuUpdatePayload(current, draft, options));
      setDetailVariant(null);
      invalidateProductSkuPickerCache();
      await loadCatalog();
    } catch (err) {
      showError(err.message);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleDetailDelete = async (variantId) => {
    if (!window.confirm(translate('products.variant_delete_confirm'))) return;
    try {
      await deleteSKU(variantId);
      setDetailVariant(null);
      invalidateProductSkuPickerCache();
      await loadCatalog();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleBulkSave = async (changedRows) => {
    setBulkSaving(true);
    try {
      await Promise.all(
        changedRows.map((row) => {
          const current = variants.find((v) => v.id === row.id);
          if (!current) return Promise.resolve();
          return updateSKU(row.id, buildSkuUpdatePayload(current, row, options));
        }),
      );
      invalidateProductSkuPickerCache();
      setBulkEditOpen(false);
      await loadCatalog();
      setSnackbar({ open: true, message: translate('products.updated'), severity: 'success' });
    } catch (err) {
      showError(err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    setConfirmDelete(false);
    try {
      await deleteProduct(id);
      invalidateProductSkuPickerCache();
      navigate('/products');
    } catch (err) {
      showError(err.message);
    }
  };

  if (!isEdit) {
    if (newProductCreateError) {
      return (
        <Box sx={{ p: 3, maxWidth: 520 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {newProductCreateError}
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              newProductAutoKickoffRef.current = false;
              creatingRef.current = false;
              void createProductAutomatically();
            }}
          >
            {translate('products.new_product_retry')}
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEdit && loadError) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => loadCatalog()}>
              {translate('home.dashboard.retry')}
            </Button>
          }
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  const pageTitle = form.name.trim() || (isEdit ? productCode : translate('products.create'));
  const viewPath = isEdit
    ? `/products/${encodeURIComponent(productCode || productId || id)}/view`
    : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Breadcrumbs sx={{ mb: 0.5 }}>
            <Link component={RouterLink} to="/products" underline="hover" color="inherit">
              {translate('products.title')}
            </Link>
            <Typography color="text.primary">{pageTitle}</Typography>
          </Breadcrumbs>
          <Typography variant="h5" component="h1">
            {pageTitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {isEdit && (
            <Button
              component={RouterLink}
              to={viewPath}
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
            >
              {translate('products.view')}
            </Button>
          )}
          {isEdit && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
              {translate('common.delete')}
            </Button>
          )}
        </Stack>
      </Box>

      {isEdit && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="cadastro" label={translate('products.form_tab_cadastro')} />
          <Tab value="alteracoes" label={translate('products.form_tab_alteracoes')} />
        </Tabs>
      )}

      {(!isEdit || tab === 'cadastro') && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <TextField
                fullWidth
                label={translate('products.product_title')}
                value={form.name}
                onChange={(e) => handleFormChange({ name: e.target.value })}
                error={Boolean(errors.name)}
                helperText={errors.name}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={translate('products.description')}
                value={form.description}
                onChange={(e) => handleFormChange({ description: e.target.value })}
              />
            </Paper>

            {isEdit && productId && simulatorEnabled && (
              <ProductVariableCostsSection
                inheritedRows={inheritedVariableCosts}
                rows={variableCostDefaults}
                onChange={handleVariableCostDefaultsChange}
              />
            )}

            {isEdit && productId && (
              <Paper sx={{ p: 2, mb: 2 }}>
                {variants.length > 0 && options.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('products.options_required_for_variants')}
                  </Alert>
                ) : null}
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.variants')}
                </Typography>
                <ProductVariantsTable
                  options={options}
                  variants={variants}
                  productName={form.name}
                  defaultVariableCosts={effectiveVariableCostDefaults}
                  groupByIndex={groupByIndex}
                  onGroupByChange={setGroupByIndex}
                  onVariantClick={setDetailVariant}
                  onVariantPatch={handleVariantPatch}
                  onApplySimulatorPrice={handleApplySimulatorPrice}
                  onEditOptions={() => setOptionsDialogOpen(true)}
                  onFilteredChange={setFilteredVariants}
                  onPurgeLegacy={() => setConfirmPurgeLegacy(true)}
                  purgingLegacy={purgingLegacy}
                  legacyCount={legacyCount}
                  legacyPreview={legacyPreview}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setOptionsDialogOpen(true)}>
                    {options.length ? translate('products.edit_options') : translate('products.add_option')}
                  </Button>
                  {filteredVariants.length > 1 && (
                    <Button size="small" onClick={() => setBulkEditOpen(true)}>
                      {translate('products.bulk_edit_variants')}
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}

            {isEdit && productId && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.image_gallery')}
                </Typography>
                <MediaGallery metadataKey="product_image" metadataValue={productId} accept="image/*" />
              </Paper>
            )}

            {isEdit && productId && (
              <CustomizationFieldsSection
                productId={productId}
                fields={customizationFields}
                onReload={loadCatalog}
                onError={showError}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                {translate('products.status')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{translate('products.status')}</InputLabel>
                <Select
                  label={translate('products.status')}
                  value={form.status}
                  onChange={(e) => handleFormChange({ status: e.target.value })}
                >
                  <MenuItem value="ACTIVE">{translate('products.active')}</MenuItem>
                  <MenuItem value="INACTIVE">{translate('products.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            {isEdit && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.categories')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {translate('products.categories_hint')}
                </Typography>
                <CategoryMultiSelect
                  selectedCategories={selectedCategories}
                  onChange={handleCategoriesChange}
                />
              </Paper>
            )}

            {isEdit && mainCategoryId && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.specifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {translate('products.specifications_hint')}
                </Typography>
                {inheritedAttributesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <ProductSpecificationsSection
                    attributes={inheritedAttributes}
                    values={attributeValues}
                    onChange={handleAttributeValueChange}
                  />
                )}
              </Paper>
            )}

            {isEdit && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.internal_code')}
                </Typography>
                <TextField fullWidth value={productCode} InputProps={{ readOnly: true }} disabled />
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {isEdit && tab === 'alteracoes' && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {translate('products.edit_history_title')}
          </Typography>
          <ProductEditHistoryTimeline events={editHistory} translate={translate} />
        </Paper>
      )}

      <VariantOptionsDialog
        open={optionsDialogOpen}
        initialOptions={options}
        onClose={() => setOptionsDialogOpen(false)}
        onSave={handleOptionsSaveRequest}
        saving={optionsSaving}
      />

      <OptionDefaultsForExistingDialog
        open={optionDefaultsOpen}
        newOptions={detectNewOptions(options, pendingOptionsDraft || [])}
        existingVariantCount={variants.length}
        onClose={() => {
          setOptionDefaultsOpen(false);
          setPendingOptionsDraft(null);
        }}
        onConfirm={handleOptionDefaultsConfirm}
      />

      <VariantDetailDialog
        open={Boolean(detailVariant)}
        variant={detailVariant}
        options={options}
        productId={productId}
        productName={form.name}
        defaultVariableCosts={effectiveVariableCostDefaults}
        productEditPath={isEdit ? `/products/${encodeURIComponent(id)}/edit` : ''}
        onClose={() => setDetailVariant(null)}
        onSave={handleDetailSave}
        onDelete={handleDetailDelete}
        onApplySimulatorPrice={handleApplySimulatorPrice}
        saving={detailSaving}
      />

      <VariantBulkEditDialog
        open={bulkEditOpen}
        variants={filteredVariants}
        options={options}
        onClose={() => setBulkEditOpen(false)}
        onSave={handleBulkSave}
        saving={bulkSaving}
      />

      <ConfirmDialog
        open={confirmPurgeLegacy}
        title={translate('products.purge_legacy_confirm_title')}
        message={translate('products.purge_legacy_confirm_message').replace('{count}', String(legacyCount))}
        confirmText={purgingLegacy ? translate('common.saving') : translate('products.purge_legacy_confirm_action')}
        cancelText={translate('common.cancel')}
        confirmColor="warning"
        onConfirm={() => {
          if (!purgingLegacy) void handlePurgeLegacy();
        }}
        onCancel={() => setConfirmPurgeLegacy(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={translate('common.confirm_delete_title')}
        message={translate('products.confirm_delete')}
        onConfirm={handleDeleteProduct}
        onCancel={() => setConfirmDelete(false)}
        confirmText={translate('common.delete')}
        cancelText={translate('common.cancel')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration ?? 6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductFormPage;
